import "./sync-dialogs.css";

import type { Conflict, Side } from "idfweb-project";

/**
 * The page's questions about a synced directory: whether to keep it in sync
 * when it is opened, which side to keep for files that changed both in the
 * browser and in the directory, what to do with a project another tab has
 * open, and where to keep a project. All are modal dialogs in the page's
 * document, drawn like the welcome window.
 */

/**
 * Whether to write changes back to `name` and watch it, or undefined when the
 * user cancelled the open.
 */
export function askToSync(name: string): Promise<boolean | undefined> {
    const { dialog, body, footer } = frame(`Open ${name}`);
    const sync = radio(
        `Save changes to ${name} and pick up changes made there (except build directory and .git)`,
        true,
    );
    const once = radio(`Copy the files into the browser once, and keep the changes there, not in ${name}`, false);
    body.append(sync.label, once.label);
    const cancel = button("Cancel");
    const open = button("Open", true);
    open.autofocus = true;
    footer.append(cancel, open);
    return show(dialog, (finish) => {
        cancel.addEventListener("click", () => finish(undefined));
        open.addEventListener("click", () => finish(sync.input.checked));
    });
}

/**
 * What to do with a project that another tab has open: open it here, which
 * stops that tab from saving it, or open a copy of it. Undefined when the
 * user cancelled.
 */
export function askAboutOtherTab(name: string): Promise<"here" | "copy" | undefined> {
    const { dialog, body, footer } = frame(`${name} is open in another tab`);
    const note = document.createElement("p");
    note.textContent =
        "One tab at a time keeps a project. Open it here, and the other tab stops saving its changes," +
        " or open a copy, kept in this browser as a project of its own.";
    body.append(note);
    const cancel = button("Cancel");
    const copy = button("Open a Copy");
    const here = button("Open Here", true);
    here.autofocus = true;
    footer.append(cancel, copy, here);
    return show(dialog, (finish) => {
        cancel.addEventListener("click", () => finish(undefined));
        copy.addEventListener("click", () => finish("copy"));
        here.addEventListener("click", () => finish("here"));
    });
}

/** The places a project can be kept: the browser's storage, or a folder on the user's computer. */
export type Place = "browser" | "folder";

/**
 * Where to keep the project `name`, which is kept at `current` now, or
 * undefined when the user leaves it there. A place the browser has no means
 * for is shown and cannot be chosen.
 */
export function askWhereToKeep(
    name: string,
    current: Place,
    options: {
        /** The tree's name, which the folder made for the project takes. */
        tree: string;
        browser: boolean;
        folder: boolean;
        /** The folder it is kept in now. */
        folderName?: string;
        /** The size of the copy in the browser, once known. */
        size?: Promise<string | undefined>;
    },
): Promise<Place | undefined> {
    const { dialog, body, footer } = frame("Project Location");
    const browser = radio("Browser storage", current === "browser");
    const folder = radio(
        current === "folder" && options.folderName !== undefined
            ? `Local folder: ${options.folderName}`
            : "Local folder",
        current === "folder",
    );
    const detail = (place: { label: HTMLLabelElement }, text: string) => {
        const span = document.createElement("span");
        span.className = "idfweb-sync-note";
        span.textContent = text;
        place.label.append(span);
        return span;
    };
    if (!options.browser) detail(browser, "Not available in this browser.");
    else if (options.size !== undefined) {
        const size = detail(browser, "");
        void options.size.then((text) => (size.textContent = text ?? ""));
    }
    if (!options.folder) detail(folder, "Not available in this browser.");
    else if (current === "browser")
        detail(folder, `A folder named ${options.tree} is created in the location you select.`);
    browser.input.disabled = !options.browser && current !== "browser";
    folder.input.disabled = !options.folder && current !== "folder";
    body.append(browser.label, folder.label);
    const cancel = button("Cancel");
    const move = button("Move", true);
    const chosen = (): Place => (browser.input.checked ? "browser" : "folder");
    const render = () => (move.disabled = chosen() === current);
    browser.input.addEventListener("change", render);
    folder.input.addEventListener("change", render);
    render();
    cancel.autofocus = true;
    footer.append(cancel, move);
    return show(dialog, (finish) => {
        cancel.addEventListener("click", () => finish(undefined));
        move.addEventListener("click", () => finish(chosen()));
    });
}

function radio(text: string, checked: boolean): { label: HTMLLabelElement; input: HTMLInputElement } {
    const label = document.createElement("label");
    label.className = "idfweb-sync-option";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "idfweb-sync-mode";
    input.checked = checked;
    label.append(input, ` ${text}`);
    return { label, input };
}

/**
 * The side to keep for each conflict. Closing the dialog keeps neither: those
 * files stay as they are on both sides until a later comparison asks again.
 */
export function askAboutConflicts(directory: string, conflicts: Conflict[]): Promise<[string, Side][]> {
    const { dialog, body, footer } = frame("Files changed in both places");
    const intro = document.createElement("p");
    intro.textContent =
        `These files changed in ${directory} and in the browser since they were last synced.` +
        " Choose which copy to keep.";
    const list = document.createElement("div");
    list.className = "idfweb-sync-conflicts";
    const choices = conflicts.map((conflict) => {
        const path = document.createElement("span");
        path.className = "path";
        path.textContent = conflict.path;
        const select = document.createElement("select");
        select.append(
            option("local", conflict.local ? `Keep ${directory}'s copy` : `Delete it, as in ${directory}`),
            option("guest", conflict.guest ? "Keep the browser's copy" : "Delete it, as in the browser"),
        );
        select.setAttribute("aria-label", conflict.path);
        list.append(path, select);
        return { path: conflict.path, select };
    });
    body.append(intro, list);
    const allLocal = button(`Keep all from ${directory}`);
    const allGuest = button("Keep all from the browser");
    const apply = button("Apply", true);
    footer.append(allLocal, allGuest, apply);
    const keep = (side: (select: HTMLSelectElement) => Side) =>
        choices.map(({ path, select }): [string, Side] => [path, side(select)]);
    return show(
        dialog,
        (finish) => {
            allLocal.addEventListener("click", () => finish(keep(() => "local")));
            allGuest.addEventListener("click", () => finish(keep(() => "guest")));
            apply.addEventListener("click", () => finish(keep((select) => select.value as Side)));
        },
        [] as [string, Side][],
    );
}

function frame(heading: string): {
    dialog: HTMLDialogElement;
    body: HTMLElement;
    footer: HTMLElement;
} {
    const dialog = document.createElement("dialog");
    dialog.className = "idfweb-welcome idfweb-sync";
    const header = document.createElement("header");
    const title = document.createElement("h1");
    title.textContent = heading;
    header.append(title);
    const body = document.createElement("div");
    body.className = "idfweb-sync-body";
    const footer = document.createElement("footer");
    dialog.append(header, body, footer);
    return { dialog, body, footer };
}

/**
 * Show the dialog until `wire`'s `finish` is called or the user closes it,
 * which answers `closed`, and remove it after.
 */
function show<T>(dialog: HTMLDialogElement, wire: (finish: (answer: T) => void) => void, closed?: T): Promise<T> {
    document.body.append(dialog);
    return new Promise((resolve) => {
        let answer = closed as T;
        dialog.addEventListener("close", () => {
            dialog.remove();
            resolve(answer);
        });
        wire((value) => {
            answer = value;
            dialog.close();
        });
        dialog.showModal();
    });
}

function button(text: string, primary = false): HTMLButtonElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = primary ? "idfweb-sync-button primary" : "idfweb-sync-button";
    element.textContent = text;
    return element;
}

function option(value: Side, text: string): HTMLOptionElement {
    const element = document.createElement("option");
    element.value = value;
    element.textContent = text;
    return element;
}
